import { useState, useEffect, useCallback } from 'react';
import { Box, TextField, MenuItem } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useBooking } from '../../../../context/bookingContext';
import useInternalBooking from '../../../../hooks/useInternalBooking';

// Maps a present booking returned by the backend into the flat customer data shape
// used by the booking context / CustomerData form.
const mapBookingToCustomerData = booking => ({
  firstName: booking.firstName || '',
  lastName: booking.lastName || '',
  mail: booking.mail || '',
  phone: booking.phone || '',
  companyName: booking.companyName || '',
  street: booking.contactData?.street || '',
  postcode:
    booking.contactData?.postcode !== undefined && booking.contactData?.postcode !== null
      ? String(booking.contactData.postcode)
      : '',
  city: booking.contactData?.city || '',
  birthdate: booking.contactData?.birthdate
    ? dayjs(booking.contactData.birthdate).format('YYYY-MM-DD')
    : '',
});

// Dropdown shown at the top of the create booking modal that lets staff copy customer data
// from a currently present customer of the selected entertainment into the new booking.
export default function CopyCustomerDataSelect() {
  const { selectedEntertainment, applyCustomerData } = useBooking();
  const { getPresentCustomersForEntertainment } = useInternalBooking();
  const [presentCustomers, setPresentCustomers] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!selectedEntertainment) {
      setPresentCustomers([]);
      setSelectedBookingId('');
      return undefined;
    }

    const fetchPresentCustomers = async () => {
      try {
        const customers = await getPresentCustomersForEntertainment(selectedEntertainment);
        if (!cancelled) {
          setPresentCustomers(Array.isArray(customers) ? customers : []);
          setSelectedBookingId('');
        }
      } catch (error) {
        if (!cancelled) {
          setPresentCustomers([]);
        }
        console.error('Error fetching present customers:', error);
      }
    };

    fetchPresentCustomers();
    return () => {
      cancelled = true;
    };
  }, [selectedEntertainment]);

  const handleSelect = useCallback(
    event => {
      const bookingId = event.target.value;
      setSelectedBookingId(bookingId);
      const booking = presentCustomers.find(entry => entry._id === bookingId);
      if (!booking) return;
      applyCustomerData(mapBookingToCustomerData(booking));
      toast.success(`Kundendaten von ${booking.firstName} ${booking.lastName} übernommen`);
    },
    [presentCustomers, applyCustomerData]
  );

  // Nothing to suggest: no entertainment selected yet or no present customers
  if (!selectedEntertainment || presentCustomers.length === 0) return null;

  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <TextField
        select
        fullWidth
        size='small'
        label='Kundendaten übernehmen von'
        value={selectedBookingId}
        onChange={handleSelect}
        InputProps={{
          startAdornment: (
            <ContentCopyIcon fontSize='small' sx={{ mr: 1, color: 'action.active' }} />
          ),
        }}
      >
        {presentCustomers.map(booking => (
          <MenuItem key={booking._id} value={booking._id}>
            {booking.firstName} {booking.lastName}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
